import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";

export default function CRMChatSessions() {
  const nav = useNavigate();
  const { adminEmail } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadSessions() {
    setLoading(true);
    try {
      const [statsRes, sessionsRes] = await Promise.all([
        fetch('/api/chatbot/stats').then(r => r.json()),
        fetch('/api/chatbot/sessions').then(r => r.json())
      ]);
      setStats(statsRes.stats);
      setSessions(sessionsRes.sessions || []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages(sessionId) {
    try {
      const res = await fetch(`/api/chatbot/sessions/${sessionId}/messages`);
      const data = await res.json();
      setMessages(data.messages || []);
      setSelectedSession(data.session);
    } catch (e) {
      console.error('Load messages error:', e);
    }
  }

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: 20 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900 }}>💬 Chat Sessions</h1>
        <p style={{ margin: "8px 0 0 0", color: "#6b7280" }}>View all chatbot conversations and captured leads</p>
      </div>

      {/* Back button and refresh */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => nav("/app/broker/crm")}
          style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
        >
          ← Back to CRM
        </button>
        <button
          onClick={loadSessions}
          style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>Total Sessions</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#3b82f6" }}>{stats.totalSessions || 0}</div>
          </div>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>Leads Captured</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#10b981" }}>{stats.leadsCapture || stats.crmLeads || 0}</div>
          </div>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>Conversion Rate</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#f59e0b" }}>{stats.conversionRate || 0}%</div>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "400px 1fr", gap: 24 }}>
        {/* Sessions List */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            Recent Conversations
          </div>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Loading...</div>
            ) : sessions.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                No chat sessions yet.<br/>
                Conversations will appear here when visitors use the chatbot.
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id || session.sessionId}
                  onClick={() => loadMessages(session.sessionId)}
                  style={{
                    padding: 16,
                    borderBottom: "1px solid #f3f4f6",
                    cursor: "pointer",
                    background: selectedSession?.sessionId === session.sessionId ? "#fef2f2" : "white",
                    transition: "background 0.2s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = selectedSession?.sessionId === session.sessionId ? "#fef2f2" : "#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background = selectedSession?.sessionId === session.sessionId ? "#fef2f2" : "white"}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {session.contact ? `${session.contact.firstName} ${session.contact.lastName}` : "Anonymous Visitor"}
                    </span>
                    {session.leadCaptured && (
                      <span style={{ fontSize: 11, padding: "2px 8px", background: "#d1fae5", color: "#065f46", borderRadius: 12, fontWeight: 600 }}>
                        LEAD
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {session.contact?.email || session.sessionId?.substring(0, 20) + "..."}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                    {session.messageCount || 0} messages • {new Date(session.createdAt || Date.now()).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages View */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>
            {selectedSession ? (
              <div>
                <div>Conversation</div>
                <div style={{ fontSize: 12, fontWeight: 400, color: "#6b7280" }}>
                  {selectedSession.contact?.email || selectedSession.sessionId}
                </div>
              </div>
            ) : (
              "Select a conversation"
            )}
          </div>
          <div style={{ padding: 16, maxHeight: 450, overflowY: "auto" }}>
            {!selectedSession ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                Select a conversation from the left to view messages
              </div>
            ) : messages.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                No messages in this session
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                      maxWidth: "80%",
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: msg.role === "user" ? "#DC2626" : "#f3f4f6",
                      color: msg.role === "user" ? "white" : "#111827",
                      fontSize: 14,
                      lineHeight: 1.5
                    }}
                  >
                    {msg.content}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info */}
      <div style={{ marginTop: 24, padding: 16, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>💡 How it works</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: "#065f46", fontSize: 14 }}>
          <li>Visitors chat with the AI assistant on your website</li>
          <li>When they provide contact info, they're automatically added to your CRM as leads</li>
          <li>All conversations are stored for review and follow-up</li>
        </ul>
      </div>
    </div>
  );
}
